import { useState } from 'react'
import { 
  Map, 
  Settings, 
  User, 
  Bell, 
  Search, 
  Database, 
  Globe,
  BarChart3,
  LogOut
} from 'lucide-react'
import { useAppStore } from '../../state/useAppStore'

const Topbar = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const { selectedCounty, drilldownUAT } = useAppStore()

  return (
    <div className="fixed top-0 left-0 right-0 z-40 h-16 bg-monster-card/80 backdrop-blur-lg border-b border-monster-border">
      <div className="h-full px-6 flex items-center justify-between">
        
        {/* Left side - Logo and Navigation */}
        <div className="flex items-center gap-8">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-monster-cyan to-monster-violet flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-monster-magenta animate-pulse" />
            </div>
            <div>
              <h1 className="monster-title text-xl font-bold">MONSTERGEO <span className="text-monster-cyan">vX</span></h1>
              <p className="text-xs text-gray-400 font-manrope">
                {selectedCounty ? 
                  `Viewing: ${selectedCounty}${drilldownUAT ? ' (UAT Details)' : ''}` : 
                  'National Overview'}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300 group">
              <Map className="w-4 h-4 text-monster-cyan" />
              <span className="text-sm font-orbitron font-medium">Map</span>
              <div className="w-2 h-2 rounded-full bg-monster-cyan animate-pulse group-hover:scale-150 transition-transform" />
            </button>

            <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300 group">
              <Database className="w-4 h-4 text-monster-magenta" />
              <span className="text-sm font-orbitron font-medium">Data</span>
            </button>

            <button className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300 group">
              <BarChart3 className="w-4 h-4 text-monster-turquoise" />
              <span className="text-sm font-orbitron font-medium">Analytics</span>
            </button>
          </div>
        </div>

        {/* Center - Search and Status */}
        <div className="flex-1 max-w-2xl mx-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search counties, UATs, agents, or insights..."
              className="w-full monster-input pl-12 pr-4 py-2.5 text-sm bg-monster-darker/50"
            />
            {searchQuery && (
              <div className="absolute inset-y-0 right-3 flex items-center">
                <div className="w-6 h-6 rounded-full bg-monster-cyan/20 flex items-center justify-center">
                  <span className="text-xs text-monster-cyan font-bold">↵</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - User and Actions */}
        <div className="flex items-center gap-4">
          {/* Status Indicators */}
          <div className="flex items-center gap-3">
            <div className="relative group">
              <div className="w-8 h-8 rounded-lg bg-monster-darker flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              </div>
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-monster-darker text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                System Online
              </div>
            </div>

            <button className="relative p-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300 group">
              <Bell className="w-5 h-5 text-gray-300" />
              <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-monster-magenta animate-pulse" />
            </button>

            <button className="relative p-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300 group">
              <Settings className="w-5 h-5 text-gray-300" />
            </button>
          </div>

          {/* User Profile */}
          <div className="flex items-center gap-3 pl-4 border-l border-monster-border">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-monster-cyan/20 to-monster-violet/20 flex items-center justify-center">
                <User className="w-5 h-5 text-monster-cyan" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-r from-monster-cyan to-monster-violet border-2 border-monster-darker" />
            </div>
            
            <div className="hidden md:block">
              <p className="text-sm font-orbitron font-medium">ADMIN</p>
              <p className="text-xs text-gray-400">Quantum Access Level</p>
            </div>

            <button className="p-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300">
              <LogOut className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Connection Status Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-monster-cyan via-monster-magenta to-monster-violet animate-pulse-glow" />
    </div>
  )
}

export default Topbar